import React, { useState, useMemo } from "react";
import { Line, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Users, UserPlus, RotateCcw, DollarSign, Eye, Plus,Pencil, Trash2 } from "lucide-react";
import { useSubscriptions } from "../../hooks/subscriptions/useSubscriptions";
import { useDashboardSubscriptions } from "../../hooks/subscriptions/useDashboardSubscriptions";
import { useCreateSubscription } from "../../hooks/subscriptions/useCreateSubscription";
import { useOrganizations } from "../../hooks/org/useOrganizations";
import { useSubscriptionPlans } from "../../hooks/subscriptions/useSubscriptionPlans";
import { useSubscription } from "../../hooks/subscriptions/useSubscription"; 
import { useUpdateSubscription } from "../../hooks/subscriptions/useUpdateSubscription";

import { useDeleteSubscription } from "../../hooks/subscriptions/useDeleteSubscription";
import FormDialog from "../../assets/FormDialog";
import "./SubscriptionManagement.css";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ArcElement, Title, Tooltip, Legend);

const SubscriptionManagement = () => {
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [viewSubscription, setViewSubscription] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [editId, setEditId] = useState(null);
  const pageSize = 10;
  const {
  subscription: selectedSubscription,
    loading: viewLoading,
    error: viewError
  } = useSubscription(viewSubscription);
  const {
    subscription: editData,
    loading: editLoading,
    error: editError
  } = useSubscription(editId);
  const {
    updateSubscription,
    loading: updateLoading,
    error: updateError
  } = useUpdateSubscription();
  const {
  deleteSubscription,
  loading: deleteLoading
} = useDeleteSubscription();

  const { subscriptions, loading, refetch } = useSubscriptions(page, pageSize);
  const { dashboardData } = useDashboardSubscriptions();
  const { createNewSubscription, loading: createLoading, error: createError } = useCreateSubscription();
  const { organizations, loading: orgLoading } = useOrganizations(
    useMemo(() => ({}), []),
    1,
    100
  );
  const { plans, loading: planLoading } = useSubscriptionPlans("");
  const getEditInitialValues = (sub) => ({
      organization: sub.organization || "",
      plan: sub.plan || "",
      status: sub.status || "trial",
      billing_cycle: sub.billing_cycle || "monthly",
      start_date: sub.start_date || "",
      end_date: sub.end_date || "",
      next_billing_date: sub.next_billing_date || "",
      payment_method: sub.payment_method || "",
      payment_status: sub.payment_status || "",
      auto_renew: sub.auto_renew ?? false,
    });

    const handleUpdate = async (data) => {
      try {
        await updateSubscription(editId, data);

        setShowModal(false);
        setEditId(null);
        refetch();
      } catch (err) {
        console.error(err);
      }
      };

  const [formData, setFormData] = useState({
    organization: "",
    plan: "",
    status: "trial",
    billing_cycle: "monthly",
    start_date: "",
    end_date: "",
    next_billing_date: "",
    payment_method: "",
    payment_status: "",
    auto_renew: false,
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (submittedData) => {
    try {
      await createNewSubscription(submittedData);
      setShowModal(false);
      setFormData({
        organization: "",
        plan: "",
        status: "trial",
        billing_cycle: "monthly",
        start_date: "",
        end_date: "",
        next_billing_date: "",
        payment_method: "",
        payment_status: "",
        auto_renew: false,
      });
      refetch();
    } catch (err) {
      console.error("Failed to create subscription:", err);
    }
  };
  const handleOpenEdit = (id) => {
    setEditId(id);
    setShowModal(true);
  };

// Placeholder delete handler — replace with real mutation later
    const handleDelete = async (id) => {
        if (!window.confirm("Delete this subscription?")) return;

        try {
          setDeletingId(id);
          await deleteSubscription(id);
          refetch();
        } finally {
          setDeletingId(null);
        }
      };

  const addSubscriptionFields = [
    {
      name: "organization",
      label: "Organization",
      type: "select",
      required: true,
      gridWidth: 6,
      group: "Subscription Details",
      options: orgLoading
        ? [{ value: "", label: "Loading..." }]
        : organizations.map((org) => ({ value: org.id, label: org.name })),
    },
    {
      name: "plan",
      label: "Plan",
      type: "select",
      required: true,
      gridWidth: 6,
      group: "Subscription Details",
      options: planLoading
        ? [{ value: "", label: "Loading..." }]
        : plans.map((p) => ({ value: p.id, label: p.name })),
    },
    {
      name: "status",
      label: "Status",
      type: "select",
      required: true,
      gridWidth: 6,
      group: "Subscription Details",
      options: [
        { value: "trial", label: "Trial" },
        { value: "active", label: "Active" },
        { value: "suspended", label: "Suspended" },
        { value: "cancelled", label: "Cancelled" },
        { value: "expired", label: "Expired" },
      ],
    },
    {
      name: "billing_cycle",
      label: "Billing Cycle",
      type: "select",
      required: true,
      gridWidth: 6,
      group: "Subscription Details",
      options: [
        { value: "monthly", label: "Monthly" },
        { value: "yearly", label: "Yearly" },
      ],
    },
    { name: "start_date", label: "Start Date", type: "date", required: true, gridWidth: 6, group: "Dates" },
    { name: "end_date", label: "End Date", type: "date", required: true, gridWidth: 6, group: "Dates" },
    { name: "next_billing_date", label: "Next Billing Date", type: "date", required: true, gridWidth: 6, group: "Dates" },
    {
      name: "payment_method",
      label: "Payment Method",
      type: "select",
      gridWidth: 6,
      group: "Payment",
      options: [
        { value: "", label: "None" },
        { value: "card", label: "Card" },
        { value: "upi", label: "UPI" },
        { value: "netbanking", label: "Net Banking" },
        { value: "wallet", label: "Wallet" },
        { value: "other", label: "Other" },
      ],
    },
    {
      name: "payment_status",
      label: "Payment Status",
      type: "select",
      gridWidth: 6,
      group: "Payment",
      options: [
        { value: "", label: "None" },
        { value: "pending", label: "Pending" },
        { value: "paid", label: "Paid" },
        { value: "failed", label: "Failed" },
      ],
    },
    { name: "auto_renew", label: "Auto Renew", type: "checkbox", gridWidth: 6, group: "Settings" },
  ];

  const viewSubscriptionFields = [
  {
    name: "organization",
    label: "Organization",
    type: "text",
    gridWidth: 6,
    group: "Subscription Details",
  },
  {
    name: "plan",
    label: "Plan Name",
    type: "text",
    gridWidth: 6,
    group: "Subscription Details",
  },
  {
    name: "plan_price",           // ← new
    label: "Plan Price",
    type: "text",
    gridWidth: 6,
    group: "Subscription Details",
  },
  {
    name: "status",
    label: "Status",
    type: "text",
    gridWidth: 6,
    group: "Subscription Details",
  },
  {
    name: "billing_cycle",
    label: "Billing Cycle",
    type: "text",
    gridWidth: 6,
    group: "Subscription Details",
  },
  {
    name: "total_monthly_charge", // ← new – very useful
    label: "Current Monthly Charge",
    type: "text",
    gridWidth: 6,
    group: "Subscription Details",
  },
  { name: "start_date", label: "Start Date", type: "text", gridWidth: 6, group: "Dates" },
  { name: "end_date", label: "End Date", type: "text", gridWidth: 6, group: "Dates" },
  { name: "next_billing_date", label: "Next Billing", type: "text", gridWidth: 6, group: "Dates" },
  {
    name: "payment_method",
    label: "Payment Method",
    type: "text",
    gridWidth: 6,
    group: "Payment",
  },
  {
    name: "payment_status",
    label: "Payment Status",
    type: "text",
    gridWidth: 6,
    group: "Payment",
  },
  {
    name: "auto_renew",
    label: "Auto Renew",
    type: "text",
    gridWidth: 6,
    group: "Settings",
  },
];

  const getViewInitialValues = (sub) => ({
    organization: sub.organization_name || "—",
    plan: sub.plan_details?.name || sub.plan_name || "—",           // ← improved
    status: sub.status?.charAt(0).toUpperCase() + sub.status?.slice(1) || "—",
    billing_cycle: sub.billing_cycle?.charAt(0).toUpperCase() + sub.billing_cycle?.slice(1) || "—",
    start_date: sub.start_date || "—",
    end_date: sub.end_date || "—",
    next_billing_date: sub.next_billing_date || "—",
    payment_method: sub.payment_method || "None",
    payment_status: sub.payment_status || "—",                      // not present → show dash
    auto_renew: sub.auto_renew ? "Yes" : "No",

    // Optional: show more useful info
    plan_price: sub.billing_cycle === "yearly"
      ? `₹${Number(sub.plan_details?.price_yearly || 0).toLocaleString()} / year`
      : `₹${Number(sub.plan_details?.price_monthly || 0).toLocaleString()} / month`,
    total_monthly_charge: sub.total_monthly_charge
      ? `₹${Number(sub.total_monthly_charge).toLocaleString()}`
      : "—",
  });

  // Modern Chart Colors (green theme)
  const revenueData = {
    labels: dashboardData?.revenue_trends?.map(t => t.month_name) || [],
    datasets: [{
      label: "Monthly Revenue",
      data: dashboardData?.revenue_trends?.map(t => t.revenue) || [],
      borderColor: "#059669",
      backgroundColor: "rgba(5, 150, 105, 0.15)",
      pointBackgroundColor: "#171516ff",
      pointBorderColor: "#fff",
      pointHoverRadius: 8,
      tension: 0.3,
      borderWidth: 2,
      fill: true,
    }]
  };

  const revenueOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { mode: "index", intersect: false },
    },
    scales: {
      x: { grid: { display: false } },
      y: {
        grid: { color: "#f1f5f9" },
        ticks: { beginAtZero: true },
      },
    },
  };

  const pieData = {
    labels: dashboardData?.subscription_distribution?.map(d => d.status) || [],
    datasets: [{
      data: dashboardData?.subscription_distribution?.map(d => d.count) || [],
      backgroundColor: ["#059669", "#10b981", "#36A2EB", "#4BC0C0", "#9966FF"],
      borderColor: "#fff",
      borderWidth: 2,
    }]
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top", labels: { font: { size: 13 } } },
    },
  };

  return (
    <div className="management-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Subscription Management</h1>
          <p className="page-subtitle">
            Overview of all active subscriptions, revenue trends and organization plans
          </p>
        </div>
        <button className="add-subscription-btn" onClick={() => setShowModal(true)}>
          <Plus size={18} /> Add Subscription
        </button>
      </div>

      {/* Add Subscription Dialog */}
     <FormDialog
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setEditId(null);
        }}
        title={editId ? "Edit Subscription" : "Create Subscription"}
        fields={addSubscriptionFields}
        initialValues={
          editId
            ? getEditInitialValues(editData || {})
            : formData
        }
        onSubmit={editId ? handleUpdate : handleSubmit}
        submitLabel={
          editId
            ? editLoading ? "Updating..." : "Update Subscription"
            : createLoading ? "Creating..." : "Create Subscription"
        }
        cancelLabel="Cancel"
        error={editError || createError || updateError}
      />

      {/* View Subscription Dialog */}
      {viewSubscription && (
        <FormDialog
          open={!!viewSubscription}
          onClose={() => setViewSubscription(null)}
          title="Subscription Details"
          fields={viewSubscriptionFields}
          initialValues={getViewInitialValues(selectedSubscription || {})}
          viewMode={true}
          submitLabel={viewLoading ? "Loading..." : "Close"}
          cancelLabel="Close"
          error={viewError}
        />
      )}

      {/* Stats Cards */}
      <div className="stats-grid">
        <StatCard
          label="Total Subscribers"
          value={dashboardData?.subscription_overview?.total_subscriptions ?? 0}
          icon={<Users size={20} />}
          color="blue"
        />
        <StatCard
          label="Active Subscriptions"
          value={dashboardData?.subscription_overview?.active_subscriptions ?? 0}
          icon={<RotateCcw size={20} />}
          color="green"
        />
        <StatCard
          label="Trial Subscriptions"
          value={dashboardData?.subscription_overview?.trial_subscriptions ?? 0}
          icon={<UserPlus size={20} />}
          color="teal"
        />
        <StatCard
          label="Total Revenue"
          value={`₹${(dashboardData?.subscription_overview?.total_revenue ?? 0).toLocaleString()}`}
          icon={<DollarSign size={20} />}
          color="purple"
        />
      </div>

      {/* Charts */}
      <div className="charts-grid">
        <div className="chart-card">
          <h3 className="chart-title">Revenue Trend</h3>
          <div className="chart-wrapper">
            <Line data={revenueData} options={revenueOptions} />
          </div>
        </div>

        <div className="chart-card">
          <h3 className="chart-title">Subscription Distribution</h3>
          <div className="chart-wrapper">
            <Pie data={pieData} options={pieOptions} />
          </div>
        </div>
      </div>

      {/* Subscribers Table */}
      <div className="table-section">
        <h2 className="section-title">Active Subscriptions</h2>

        <div className="table-container">
          {loading ? (
            <div className="loading-state">Loading subscriptions...</div>
          ) : (
            <div className="table-scroll-wrapper">
              <table className="subscribers-table">
                <thead>
                  <tr>
                    <th>Organization</th>
                    <th>Plan</th>
                    <th>Status</th>
                    <th>Billing</th>
                    <th>Start Date</th>
                    <th>Next Billing</th>
                    <th>Amount</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.length > 0 ? (
                    subscriptions.map((sub) => (
                      <tr key={sub.id}>
                        <td className="org-name">{sub.organization_name}</td>
                        <td>
                          <span className={`plan-badge ${sub.plan_name?.toLowerCase()}`}>
                            {sub.plan_name || "—"}
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge status-${sub.status}`}>
                            {sub.status?.charAt(0).toUpperCase() + sub.status?.slice(1) || "—"}
                          </span>
                        </td>
                        <td>{sub.billing_cycle || "—"}</td>
                        <td>{sub.start_date || "—"}</td>
                        <td>{sub.next_billing_date || "—"}</td>
                        <td className="amount">₹{sub.total_monthly_charge?.toLocaleString() || "0"}</td>
                        <td className="actions-cell">
                        <div className="action-buttons">
                          <button
                            className="view-action"
                            onClick={() => setViewSubscription(sub.id)}
                            title="View details"
                          >
                            <Eye size={18} />
                          </button>
                          
                          <button
                            className="edit-action"
                            onClick={() => handleOpenEdit(sub.id)}
                            title="Edit subscription"
                          >
                            <Pencil size={18} />
                          </button>
                          
                         <button
                            className="delete-action"
                            onClick={() => handleDelete(sub.id)}
                            disabled={deletingId === sub.id}
                            title="Delete subscription"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="no-data">
                        No subscriptions found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Simple Pagination */}
        <div className="pagination">
          <button
            className="pagination-btn"
            disabled={page === 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
          >
            Previous
          </button>
          <span className="page-info">Page {page}</span>
          <button
            className="pagination-btn"
            onClick={() => setPage(p => p + 1)}
            disabled={subscriptions.length < pageSize}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

// Reusable Stat Card
const StatCard = ({ label, value, icon, color }) => (
  <div className={`stat-card ${color}`}>
    <div className="stat-content">
      <p className="stat-label">{label}</p>
      <h3 className="stat-value">{value}</h3>
    </div>
    <div className="stat-icon-wrapper">
      <div className="stat-icon">{icon}</div>
    </div>
  </div>
);

export default SubscriptionManagement;